import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { useState } from 'react';
import { toast } from 'sonner';
import { invalidateAccountAdminQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import {
	TRIAL_EXTENSION_DAYS,
	TrialExtensionDays,
	useExtendTrialMutation,
} from '../../../../queries/Admin/Account/useExtendTrialMutation';
import { useRemovePendingCancellationMutation } from '../../../../queries/Admin/Account/useRemovePendingCancellationMutation';
import { Button } from '../../../../web/components-v2/Button';
import { Dialog, DialogBody, DialogFooter, DialogPopup, DialogTitle } from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { getDefaultDateStringValue } from '../../../../models/utils/dateUtils';
import { billingRenewalDialogsStyles as s } from './billingRenewalDialogsStyles';
import { isTrialExpired } from '../accountOverviewFormatters';

/**
 * Confirms taking an account off the cancel-request list. A reason is mandatory, as legacy required.
 *
 * Deliberately no still-in-contract warning: legacy shows that on the *cancel* dialog, where
 * cancelling early is the risk. Un-cancelling an in-contract account is the corrective action, so
 * warning here would discourage the right thing.
 */
export const StopCancellationDialog = ({
	account,
	open,
	onClose,
}: {
	account: Api.IAccount;
	open: boolean;
	onClose: () => void;
}) => {
	const [reason, setReason] = useState('');
	const mutation = useRemovePendingCancellationMutation();

	const submit = async () => {
		try {
			await mutation.mutateAsync({ accountId: account.id, reason: reason.trim() });
		} catch {
			// The mutation's own onError raises the toast; swallow here so a failed billing write leaves
			// the dialog open with the reason intact to retry, rather than rejecting out of the handler.
			return;
		}
		invalidateAccountAdminQuery();
		toast.success(`The ${account.companyName} account has been removed from the cancel request list.`);
		onClose();
	};

	return (
		<Dialog open={open} onOpenChange={next => !next && onClose()} dismissOnOutsideClick={false}>
			<DialogPopup styles={[s.popup]}>
				<DialogTitle>Stop this cancellation?</DialogTitle>
				<DialogBody styles={[s.body]}>
					<span className={css(s.prompt)}>
						{`${account.companyName} is pending cancellation. Removing them from the review list means their account will not be cancelled by the management/accounting team.`}
					</span>
					<Input
						value={reason}
						placeholder='Reason for un-cancelling?'
						onChange={e => setReason(e.target.value)}
						disabled={mutation.isPending}
					/>
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' onClick={onClose} disabled={mutation.isPending}>
						Cancel
					</Button>
					<Button onClick={submit} isLoading={mutation.isPending} disabled={!reason.trim()}>
						Stop Cancellation
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

/** Extends a trial by one of the legacy durations (7, 14 or 30 days). */
export const ExtendTrialDialog = ({
	account,
	open,
	onClose,
}: {
	account: Api.IAccount;
	open: boolean;
	onClose: () => void;
}) => {
	const [days, setDays] = useState<TrialExtensionDays>(TRIAL_EXTENSION_DAYS[0]);
	const mutation = useExtendTrialMutation();

	const currentExpiration = account.planDetails?.trialExpirationDate;
	const expired = isTrialExpired(account);
	// Legacy extends from the expiration date while it is still in the future, otherwise from today —
	// which is what "extend it for 7, 14, or 30 days from today" in the expired copy refers to.
	const extendFrom =
		currentExpiration && moment(currentExpiration).isAfter(moment()) ? moment(currentExpiration) : moment();
	const extendedExpiration = extendFrom.clone().add(days, 'days').toDate();

	const submit = async () => {
		try {
			await mutation.mutateAsync({ accountId: account.id, durationInDays: days });
		} catch {
			// As above: the mutation toasts the failure, and the dialog stays open to retry.
			return;
		}
		invalidateAccountAdminQuery();
		toast.success(`${account.companyName}'s trial was extended by ${days} days.`);
		onClose();
	};

	return (
		<Dialog open={open} onOpenChange={next => !next && onClose()} dismissOnOutsideClick={false}>
			<DialogPopup styles={[s.popup]}>
				<DialogTitle>Extend this trial</DialogTitle>
				<DialogBody styles={[s.body]}>
					{/* Legacy shows both the current expiration (worded for whether it has already passed) and
						    the date the chosen extension lands on, so the consequence is visible before saving.
						    A trial can reach here with no stored expiration, which has no date to word either way. */}
					{currentExpiration ? (
						<span className={css(s.prompt)}>
							{expired ? 'Trial expired on: ' : 'Current expiration date: '}
							<span className={css(s.emphasis)}>{getDefaultDateStringValue(currentExpiration)}</span>
						</span>
					) : (
						<span className={css(s.prompt)}>This trial has no expiration date on file.</span>
					)}
					<div className={css(s.dayOptions)}>
						{TRIAL_EXTENSION_DAYS.map(option => (
							<Button
								key={option}
								variant={option === days ? 'primary' : 'secondary'}
								onClick={() => setDays(option)}
								disabled={mutation.isPending}
							>
								{`${option} days`}
							</Button>
						))}
					</div>
					<span className={css(s.prompt)}>
						{'New expiration date: '}
						<span className={css(s.emphasis)}>{getDefaultDateStringValue(extendedExpiration)}</span>
					</span>
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' onClick={onClose} disabled={mutation.isPending}>
						Cancel
					</Button>
					<Button onClick={submit} isLoading={mutation.isPending}>
						Extend Trial
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
